import { TFunction } from 'i18next'
import { z } from 'zod'

import { SelectOption } from '@/components/ui/select'
import { PackageCarrier, PackageCarrierSchema } from '@/models'

const makePackageFormBaseSchema = (t: TFunction<['packages']>) =>
  z.object({
    carrier: z.enum(PackageCarrierSchema.options, {
      message: t('packages:packageFormModal.validation.carrierRequired'),
    }),
    comment: z
      .string()
      .max(200, t('packages:packageFormModal.validation.commentMaxLength'))
      .optional(),
    image: z.array(z.instanceof(File)).optional(),
    location: z.string().optional(),
    perishable: z.boolean(),
    residentId: z.number({
      message: t('packages:packageFormModal.validation.residentRequired'),
    }),
    trackingNumber: z.string().max(40).optional(),
  })

export const PACKAGE_IMAGE_ACCEPTED_TYPES = ['JPG', 'JPEG', 'PNG']

export type PackageFormData = z.infer<ReturnType<typeof makePackageFormBaseSchema>>

export const createPackageFormSchema = (
  t: TFunction<['packages']>,
  settings: {
    trackingNumberRequired?: boolean
    locationRequired?: boolean
    photoRequired?: boolean
    hasExistingImage?: boolean
  },
) => {
  return makePackageFormBaseSchema(t).superRefine((data, ctx) => {
    if (settings.trackingNumberRequired && !data.trackingNumber) {
      ctx.addIssue({
        code: 'custom',
        message: t('packages:packageFormModal.validation.trackingNumberRequired'),
        path: ['trackingNumber'],
      })
    }
    if (settings.locationRequired && !data.location) {
      ctx.addIssue({
        code: 'custom',
        message: t('packages:packageFormModal.validation.locationRequired'),
        path: ['location'],
      })
    }
    if (settings.photoRequired && !data.image?.length && !settings.hasExistingImage) {
      ctx.addIssue({
        code: 'custom',
        message: t('packages:packageFormModal.validation.photoRequired'),
        path: ['image'],
      })
    }
  })
}

export const getCarrierOptions = (t: TFunction<['packages']>): SelectOption<PackageCarrier>[] => [
  { label: t('packages:carrierOptions.fedex'), value: PackageCarrierSchema.enum.fedex },
  { label: t('packages:carrierOptions.ups'), value: PackageCarrierSchema.enum.ups },
  { label: t('packages:carrierOptions.usps'), value: PackageCarrierSchema.enum.usps },
  { label: t('packages:carrierOptions.dhl'), value: PackageCarrierSchema.enum.dhl },
  { label: t('packages:carrierOptions.amazon'), value: PackageCarrierSchema.enum.amazon },
  { label: t('packages:carrierOptions.other'), value: PackageCarrierSchema.enum.other },
]

export const getPerishableOptions = (t: TFunction<['packages']>): SelectOption<boolean>[] => [
  { label: t('packages:yesNo.yes'), value: true },
  { label: t('packages:yesNo.no'), value: false },
]
